import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../../firebase/config';
import {
  doc, setDoc, addDoc, collection, onSnapshot,
  query, orderBy, serverTimestamp, updateDoc, increment,
} from 'firebase/firestore';

// ── Admin-side Support Panel ──────────────────────────────────────────────────
// A right-edge slide-in drawer for company admins to chat with the super admin
// about technical issues or feature requests. One thread per company, keyed by
// companyId so it's always the same conversation (no ticket IDs to juggle).
// Super admin replies from the Support inbox in the Super Admin Portal.
// Messages are NOT encrypted — this is a support channel, not private chat.
export default function SupportPanel({ companyId, companyName, currentUser, onClose }) {
  const [messages, setMessages] = useState([]);
  const [thread,   setThread]   = useState(null);
  const [text,     setText]     = useState('');
  const [sending,  setSending]  = useState(false);
  const bottomRef = useRef(null);

  // Subscribe to thread metadata (unread count, status)
  useEffect(() => {
    if (!companyId) return;
    return onSnapshot(doc(db, 'supportThreads', companyId), snap => {
      setThread(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
  }, [companyId]);

  // Subscribe to messages real-time
  useEffect(() => {
    if (!companyId) return;
    const q = query(
      collection(db, 'supportThreads', companyId, 'messages'),
      orderBy('sentAt', 'asc'),
    );
    return onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [companyId]);

  // Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Mark unreadAdmin → 0 whenever the panel is open
  useEffect(() => {
    if (!companyId || !thread || !(thread.unreadAdmin > 0)) return;
    updateDoc(doc(db, 'supportThreads', companyId), { unreadAdmin: 0 }).catch(() => {});
  }, [companyId, thread]);

  const send = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      // Upsert thread — companyId is the document ID so there's always exactly
      // one thread per company (no ambiguity about "which ticket").
      await setDoc(doc(db, 'supportThreads', companyId), {
        companyId,
        companyName: companyName || companyId,
        adminEmail: currentUser?.email || '',
        adminName:  currentUser?.name  || '',
        status: 'open',
        lastMessageAt:   serverTimestamp(),
        lastMessageText: trimmed.slice(0, 120),
        unreadAdmin:      0,
        unreadSuperAdmin: increment(1),
      }, { merge: true });
      await addDoc(collection(db, 'supportThreads', companyId, 'messages'), {
        text: trimmed,
        senderType: 'admin',
        senderName: currentUser?.name || currentUser?.email || 'Admin',
        sentAt: serverTimestamp(),
      });
      setText('');
    } finally {
      setSending(false);
    }
  }, [text, sending, companyId, companyName, currentUser]);

  const resolved = thread?.status === 'resolved';

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, maxWidth: '95vw',
      background: '#fff', zIndex: 250, display: 'flex', flexDirection: 'column',
      boxShadow: '-8px 0 48px rgba(0,0,0,0.18)', borderRadius: '16px 0 0 16px',
      fontFamily: "'Inter', sans-serif",
    }}>

      {/* Header */}
      <div style={{
        padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12,
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        borderRadius: '16px 0 0 0', flexShrink: 0,
      }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10, flexShrink: 0,
          background: 'rgba(245,158,11,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
        }}>🎧</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Platform Support</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 1 }}>
            {resolved ? '✅ Resolved' : '🟢 We\'re here to help'}
          </div>
        </div>
        {!resolved ? (
          <button
            onClick={() => updateDoc(doc(db, 'supportThreads', companyId), { status: 'resolved' }).catch(() => {})}
            style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.55)', fontSize: 10, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
            Mark resolved
          </button>
        ) : (
          <button
            onClick={() => updateDoc(doc(db, 'supportThreads', companyId), { status: 'open' }).catch(() => {})}
            style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.1)', color: '#10b981', fontSize: 10, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
            Reopen
          </button>
        )}
        <button onClick={onClose}
          style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', padding: '6px 10px', borderRadius: 8, fontSize: 14, flexShrink: 0 }}>
          ✕
        </button>
      </div>

      {/* Welcome banner shown when no messages yet */}
      {messages.length === 0 && (
        <div style={{ padding: '18px 20px', background: 'linear-gradient(135deg, rgba(245,158,11,0.06), rgba(124,58,237,0.04))', borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 5 }}>👋 How can we help?</div>
          <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.65 }}>
            Tell us about a technical issue or feature request. Our team typically responds within a few hours.
          </div>
          <div style={{ display: 'flex', gap: 7, marginTop: 10, flexWrap: 'wrap' }}>
            {["I found a bug 🐞", "Feature request 💡", "Setup help ⚙️"].map(t => (
              <button key={t} onClick={() => setText(t)}
                style={{ padding: '5px 11px', borderRadius: 20, border: '1px solid rgba(124,58,237,0.2)', background: 'rgba(124,58,237,0.06)', color: '#7c3aed', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map(msg => {
          const isMine = msg.senderType === 'admin';
          return (
            <div key={msg.id} style={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '80%',
                background: isMine ? 'linear-gradient(135deg, #7c3aed, #3b82f6)' : '#f1f5f9',
                color: isMine ? '#fff' : '#0f172a',
                borderRadius: isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                padding: '10px 14px', fontSize: 13, lineHeight: 1.55,
                boxShadow: isMine ? '0 2px 8px rgba(124,58,237,0.22)' : '0 1px 3px rgba(0,0,0,0.06)',
              }}>
                {!isMine && (
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', marginBottom: 4, letterSpacing: 0.4 }}>PLATFORM SUPPORT</div>
                )}
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.text}</div>
                <div style={{ fontSize: 10, marginTop: 5, opacity: 0.55, textAlign: isMine ? 'right' : 'left' }}>
                  {msg.sentAt?.seconds
                    ? new Date(msg.sentAt.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : '…'}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      {!resolved ? (
        <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(0,0,0,0.07)', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={'Describe your issue or request…\nEnter to send · Shift+Enter for newline'}
              rows={3}
              style={{
                flex: 1, resize: 'none', border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: 11, padding: '10px 13px', fontSize: 13,
                fontFamily: 'inherit', outline: 'none', lineHeight: 1.55,
                color: '#0f172a', background: '#fafafa',
              }}
            />
            <button onClick={send} disabled={!text.trim() || sending}
              style={{
                padding: '12px 16px', borderRadius: 11, border: 'none',
                background: text.trim() && !sending
                  ? 'linear-gradient(135deg, #7c3aed, #3b82f6)'
                  : '#e2e8f0',
                color: text.trim() && !sending ? '#fff' : '#94a3b8',
                fontWeight: 700, cursor: text.trim() && !sending ? 'pointer' : 'default',
                fontSize: 16, flexShrink: 0, transition: 'all 0.15s',
                boxShadow: text.trim() ? '0 4px 12px rgba(124,58,237,0.28)' : 'none',
              }}>
              {sending ? '…' : '→'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ padding: '14px 20px', background: 'rgba(16,185,129,0.06)', borderTop: '1px solid rgba(16,185,129,0.12)', fontSize: 12, color: '#059669', textAlign: 'center', fontWeight: 600, flexShrink: 0 }}>
          ✅ Ticket resolved — click <em>Reopen</em> above to continue the conversation
        </div>
      )}
    </div>
  );
}
